// 家用面板 L1 小工具版面（docs/panel 07-dashboard §5.4、§7）：版面歷史與還原。只限主人。
//   - 最近 5 版（時間、在面板上 / 在 App 上 / 預設、幾個小工具），目前那版標「目前」；還原回舊版後同內容的舊列標「跟目前一樣」
//   - 每個非目前的版本［還原這一版］、卡片上方［還原上一版］（version = "previous"）→ 確認 → 成功重抓歷史
//   - 推播 panel.layout_changed 點進來（/devices/{id}#layout）→ 捲到這張卡片
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { formatTimestampS } from '../../lib/format';
import { ConfirmDialog } from './settings/widgets';
import { getLayoutHistory, hasPreviousLayout, LAYOUT_CARD_ANCHOR, layoutFromKey, restoreLayout, type PanelLayoutVersion } from './panels';

/** #layout 捲過去後，頁面還在長高（上面的卡片晚載好）就跟著對齊多久 */
export const ANCHOR_HOLD_MS = 3000;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/** 要還原哪一版：歷史裡某一列，或 previous（上一版） */
type Target = { version: string; row?: PanelLayoutVersion };

export function PanelLayoutCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<PanelLayoutVersion[] | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => getLayoutHistory(panelId).then(setRows).catch(() => {
    setRows([]);
    setMsg(t('panels.errGeneric'));
  }), [panelId, t]);
  useEffect(() => void load(), [load]);
  // 推播點進來：等歷史載好（卡片高度定了）再捲。上面的卡片（行事曆、相簿…）晚一點才載好會把這張往下推 →
  // 捲過去後 ANCHOR_HOLD_MS 內頁面長高就再對齊一次；使用者自己滑 / 按鍵就不再動
  const anchored = rows !== null && hash === `#${LAYOUT_CARD_ANCHOR}`;
  useEffect(() => {
    if (!anchored) return;
    ref.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    if (typeof ResizeObserver === 'undefined') return;
    let done = false;
    const stop = () => {
      if (done) return;
      done = true;
      ro.disconnect();
      clearTimeout(timer);
      for (const ev of USER_SCROLL_EVENTS) window.removeEventListener(ev, stop);
    };
    const ro = new ResizeObserver(() => {
      if (!done) ref.current?.scrollIntoView?.({ block: 'start' });
    });
    ro.observe(document.body);
    const timer = setTimeout(stop, ANCHOR_HOLD_MS);
    for (const ev of USER_SCROLL_EVENTS) window.addEventListener(ev, stop, { passive: true });
    return stop;
  }, [anchored]);

  const restore = async (tg: Target) => {
    setBusy(true);
    setMsg(null);
    try {
      await restoreLayout(panelId, tg.version);
      setMsg(t('panels.layout.restored'));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'LAYOUT_VERSION_NOT_FOUND') setMsg(t('panels.layout.errGone'));
      else if (e instanceof ApiError && e.status === 403) setMsg(t('panels.layout.errForbidden'));
      else setMsg(t('panels.errGeneric'));
    } finally {
      setTarget(null);
      setBusy(false);
      void load();
    }
  };

  const when = (r: PanelLayoutVersion) => formatTimestampS(r.at, { second: undefined });
  return (
    <div className="card dv-layout-card" id={LAYOUT_CARD_ANCHOR} ref={ref} data-testid="panel-layout">
      <h2>{t('panels.layout.title')}</h2>
      <p className="text-secondary">{t('panels.layout.hint')}</p>
      {rows && hasPreviousLayout(rows) && (
        <div className="dv-pairing-actions">
          <button className="btn-ghost" onClick={() => setTarget({ version: 'previous' })} disabled={busy} data-testid="layout-restore-previous">
            {t('panels.layout.restorePrevious')}
          </button>
        </div>
      )}
      {rows && rows.length === 0 && !msg && <p className="text-secondary">{t('panels.layout.empty')}</p>}
      {rows && rows.length > 0 && (
        <ul className="dv-layout-list">
          {rows.map((r, i) => (
            <li key={`${r.version}-${r.at}-${i}`} className="dv-layout-row" data-testid={`layout-row-${i}`} data-version={r.version}>
              <div className="dv-layout-meta">
                <span>{when(r)}</span>
                <span className="text-secondary">
                  {t(layoutFromKey(r.from))} · {t('panels.layout.widgets', { count: r.widgets })}
                </span>
              </div>
              {r.is_current ? (
                <span className="dv-badge" data-testid="layout-current">{t('panels.layout.current')}</span>
              ) : r.same_as_current ? (
                <span className="text-secondary" data-testid="layout-same">{t('panels.layout.sameAsCurrent')}</span>
              ) : (
                <button className="btn-ghost" onClick={() => setTarget({ version: r.version, row: r })} disabled={busy}>{t('panels.layout.restoreThis')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {msg && <p className="text-secondary" role="status">{msg}</p>}

      {target && (
        <ConfirmDialog
          spec={{
            title: t(target.row ? 'panels.layout.confirmTitle' : 'panels.layout.confirmPreviousTitle'),
            body: target.row
              ? t('panels.layout.confirmBody', { time: when(target.row), from: t(layoutFromKey(target.row.from)) })
              : t('panels.layout.confirmPreviousBody'),
            confirmLabel: t('panels.layout.confirmYes'),
          }}
          busy={busy}
          onCancel={() => setTarget(null)}
          onConfirm={() => void restore(target)}
        />
      )}
    </div>
  );
}
